import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#prepareColorPreviews();
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  static #preloadedColors = new Map();
  #selection = 0;
  #previewMap;

  #prepareColorPreviews() {
    const source = this.querySelector('[data-color-preview-map]');
    if (!source) return;
    try {
      this.#previewMap = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (navigator.connection?.saveData) return;
    const preload = () => {
      if (!this.isConnected) return;
      const warm = () => {
        if (!this.isConnected) return;
        for (const template of Array.from(this.querySelectorAll('template[data-color-preview]')).slice(0, 12)) {
          const sourceImage = template.content.querySelector('img');
          if (!sourceImage) continue;
          const key = sourceImage.srcset + sourceImage.sizes;
          if (MediaGallery.#preloadedColors.has(key)) continue;
          const image = new Image();
          image.fetchPriority = 'low';
          image.decoding = 'async';
          image.sizes = sourceImage.sizes;
          image.srcset = sourceImage.srcset;
          image.src = sourceImage.src;
          MediaGallery.#preloadedColors.set(key, image);
          if (MediaGallery.#preloadedColors.size > 60) {
            MediaGallery.#preloadedColors.delete(MediaGallery.#preloadedColors.keys().next().value);
          }
        }
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 1500 });
      else window.setTimeout(warm, 0);
    };
    const mainImage = this.querySelector('.product-media__image');
    if (!mainImage || mainImage.complete) preload();
    else mainImage.addEventListener('load', preload, { once: true, signal: this.#controller.signal });
  }

  #previewColor(picker) {
    if (!this.#previewMap || typeof picker.getAllSelectedOptions !== 'function') return;
    if (picker.querySelector('input:checked[data-connected-product-url]:not([data-connected-product-url=""]), option:checked[data-connected-product-url]:not([data-connected-product-url=""])')) return;
    const options = picker.getAllSelectedOptions();
    if (!options.length) return;
    const variant = this.#previewMap.variants.find((variant) => options.every((option) => {
      const index = this.#previewMap.options.indexOf(option.name);
      return index >= 0 && variant.options[index] === option.value;
    }));
    if (!variant?.media) return;
    const template = Array.from(this.querySelectorAll('template[data-color-preview]')).find((item) => item.dataset.colorPreview === String(variant.media));
    const source = template?.content.querySelector('.product-media');
    const sourceImage = source?.querySelector('img');
    if (!sourceImage) return;
    for (const media of this.querySelectorAll('.product-media')) {
      if (media.dataset.mediaId === String(variant.media)) continue;
      const image = media.querySelector('.product-media__image');
      if (!image) continue;
      media.dataset.mediaId = String(variant.media);
      media.style.setProperty('--ratio', source.style.getPropertyValue('--ratio'));
      const container = media.closest('.product-media-container');
      container?.style.setProperty('--media-preview-ratio', source.style.getPropertyValue('--ratio'));
      for (const attribute of ['alt', 'width', 'height', 'data-max-resolution', 'style', 'srcset', 'src']) {
        const value = sourceImage.getAttribute(attribute);
        if (value !== null) image.setAttribute(attribute, value);
      }
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selection = ++this.#selection;
    this.#previewColor(event.target);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || selection !== this.#selection || !this.isConnected) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

